import React from "react";
import styled from "@emotion/styled";
import {
  Box as MuiBox,
  Stack as MuiStack,
  Typography as MuiTypography,
  useTheme,
  Button,
} from "@mui/material";
import useAIDTMediaQuery from "../../hooks/useAIDTMediaQuery";
import ScrollMoreIcon from "../../icons/ScrollMoreIcon";
import { LayoutPagination } from "./layout-pagination";
import { LayoutFooterPaging, LayoutFooterSubmit } from "./Footer";
import { LayoutHeaderSubTitle, LayoutHeaderTitle } from "./Header";

import type { BoxProps, StackProps, SxProps, Theme, TypographyProps } from "@mui/material";

interface EliceLayoutContextProps {
  isScrollActive: boolean;
  setIsScrollActive: (isSticky: boolean) => void;
  enableScrollTracking: boolean;
}

const EliceLayoutContext = React.createContext<EliceLayoutContextProps | undefined>(undefined);

export interface EliceLayoutProps {
  children: React.ReactNode;
  backgroundImage?: string;
  backgroundColor?: string;
  onPrev?: () => void;
  onNext?: () => void;
  allowPrev?: boolean;
  allowNext?: boolean;
  onlyPage?: boolean;
  enableScrollTracking?: boolean;
  sx?: SxProps<Theme>;
}

type Alignment = "left" | "center" | "right";

interface EliceStepProps<T extends React.ElementType> {
  step?: -1 | 0 | 1 | 2 | 3;
  component?: T;
}
type EliceBoxProps<T extends React.ElementType> = BoxProps<T> &
  EliceStepProps<T>;
type EliceStackProps<T extends React.ElementType> = StackProps<T> &
  EliceStepProps<T>;
type EliceTypographyProps = TypographyProps &
  Omit<EliceStepProps<React.ElementType>, 'component'>;

/**
 *  @summary Alpha of Design System. Wrapper of MUI Box
 * */
export const Box = React.forwardRef(function Box<T extends React.ElementType>({
  component,
  step,
  sx,
  ...props
}: EliceBoxProps<T>, ref: React.Ref<unknown>) {
  const stepSx = {
    ...(step !== undefined ? ({
      '--fluid-size': `var(--step-${step})`,
      '& *:not([class*=Typography]):not([class*=Typography] *)': {
        'font-size': 'var(--fluid-size)',
      }
    }) : {}),
    ...sx
  };

  return <MuiBox ref={ref} component={component} sx={stepSx} {...props} />;
});

/**
 *  @summary Alpha of Design System. Wrapper of MUI Stack
 * */
export const Stack = React.forwardRef(function Stack<T extends React.ElementType>({
  component,
  step,
  sx,
  ...props
}: EliceStackProps<T>, ref: React.Ref<unknown>) {
  const stepSx = {
    ...(step !== undefined ? ({
      '--fluid-size': `var(--step-${step})`,
      '& *:not([class*=Typography]):not([class*=Typography] *)': {
        'font-size': 'var(--fluid-size)',
      }
    }) : {}),
    ...sx,
  };

  return (
    <MuiStack
      ref={ref}
      // FIXME: delete any in React 19.
      component={component as any}
      sx={stepSx}
      {...props}
    />
  );
});


/**
 *  @summary Alpha of Design System. Wrapper of MUI Typography
 * */
export function Typography({ step, sx, ...props }: EliceTypographyProps) {
  const stepSx = {
    '&:is([class*="Typography"])': {
      ...(sx ?? ({} as any)),
      '--fluid-size': `var(--step-${step ?? 0})`,
      fontSize: (sx as any)?.fontSize,
    },
  };

  return <MuiTypography sx={stepSx} {...props} />;
}


export const Layout = ({
  children,
  onPrev,
  onNext,
  allowPrev,
  allowNext,
  backgroundImage,
  backgroundColor,
  onlyPage = false,
  enableScrollTracking = false,
  sx
}: EliceLayoutProps) => {
  const { isTablet, isMiniTablet } = useAIDTMediaQuery();
  const [isScrollActive, setIsScrollActive] = React.useState(false);

  return (
    <EliceLayoutContext.Provider value={{ isScrollActive, setIsScrollActive, enableScrollTracking }}>
      <MainContainer
        isMiniTablet={isMiniTablet}
        backgroundImage={backgroundImage}
        backgroundColor={backgroundColor}
        sx={sx}
      >
        {
          !onlyPage && (
            !isTablet && (
              <LayoutPagination onPrev={onPrev} onNext={onNext} allowPrev={allowPrev} allowNext={allowNext} />
            )
          )
        }
        {children}
      </MainContainer>
    </EliceLayoutContext.Provider>
  );
}

const HeaderContainerInner = styled(Stack)`
  height: 4.625rem;
  width: 100%;
  justify-content: space-between;
  flex-direction: row;
`;
export const HeaderContainer = ({ children, sx }: { children: React.ReactNode, sx?: SxProps<Theme> }) => {
  return <HeaderContainerInner sx={sx}>{children}</HeaderContainerInner>;
}
export const HeaderTitle = LayoutHeaderTitle;
export const HeaderSubTitle = LayoutHeaderSubTitle;

const ContentContainerInner = styled(Stack) <{ theme: Theme }>`
  flex: 1;
  width: 100%;
  max-height: calc(100vh - 4.625rem - 5rem);
  overflow-y: auto;
  min-height: 0;
  position: relative;

  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.palette.primary.main};
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-track {
    background-color: ${({ theme }) => theme.palette.grey[300]};
    border-radius: 0.25rem;
  }
`;
export const ContentContainer = ({
  children,
  sx,
  scrollbarPostion = 10
}: {
  children: React.ReactNode,
  sx?: SxProps<Theme>,
  scrollbarPostion?: number
}) => {
  const theme = useTheme();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const context = React.useContext(EliceLayoutContext);

  if (!context) {
    throw new Error("EliceLayout components must be used within an EliceLayout provider.");
  }

  const { isScrollActive, setIsScrollActive, enableScrollTracking } = context;
  const [hasScroll, setHasScroll] = React.useState(false);
  const scrollTopRef = React.useRef(0);
  const scrollBarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!enableScrollTracking) return;

    const element = contentRef.current;
    if (!element) return;

    const handleScroll = () => {
      setIsScrollActive(element.scrollTop > 10);
    };

    element.addEventListener('scroll', handleScroll);

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [enableScrollTracking, setIsScrollActive]);

  // ✅ 2. 스크롤 위치 계산용 useEffect (커스텀 스크롤바용)
  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const updateScrollBar = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;

      // 상태로 관리하지 않고 직접 반영
      scrollTopRef.current = scrollTop;

      const thumbHeight = (clientHeight / scrollHeight) * clientHeight;
      const top = (scrollTop / scrollHeight) * clientHeight;

      if (scrollBarRef.current) {
        scrollBarRef.current.style.height = `${thumbHeight}px`;
        scrollBarRef.current.style.top = `${top}px`;
      }

      setHasScroll(scrollHeight > clientHeight);
    };

    el.addEventListener('scroll', updateScrollBar);

    requestAnimationFrame(() => {
      if (el.scrollHeight > el.clientHeight) {
        el.scrollTop = 1;         // 👉 살짝 움직여줌
        el.scrollTop = 0;         // 👉 바로 다시 원위치
        updateScrollBar();        // 👉 thumb도 즉시 업데이트
      }
    });

    return () => el.removeEventListener('scroll', updateScrollBar);
  }, []);

  return (
    <ContentContainerWrapper>
      <ContentContainerInner
        ref={contentRef as any}
        theme={theme}
        flex={1}
        sx={{
          ...sx,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {children}
        {enableScrollTracking && hasScroll && !isScrollActive && (
          <ScrollMoreButton>
            <ScrollMoreIcon size={32} color="white" bgOpacity={0.5} />
          </ScrollMoreButton>
        )}
      </ContentContainerInner>

      {/* 커스텀 스크롤바 */}
      {hasScroll && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: `${-1 * scrollbarPostion}px`,
            width: '0.5rem',
            height: '100%',
            backgroundColor: '#eee',
            borderRadius: '0.25rem',
          }}
        >
          <div
            ref={scrollBarRef}
            style={{
              width: '100%',
              backgroundColor: theme.palette.primary.main,
              borderRadius: '0.25rem',
              position: 'absolute',
              transition: 'top 0.01s linear',
            }}
          />
        </div>
      )}
    </ContentContainerWrapper>
  );
}
export const Subtitle = ({ children, alignment = "left", justifyContent = "flex-start", sx }:
  {
    children: React.ReactNode;
    alignment?: Alignment;
    justifyContent?: string;
    sx?: SxProps<Theme>
  }) => {
  const context = React.useContext(EliceLayoutContext);
  if (!context) {
    throw new Error("EliceLayout components must be used within an EliceLayout provider.");
  }
  const { isScrollActive, enableScrollTracking } = context;

  return (
    <SubtitleContainer
      isSticky={enableScrollTracking && isScrollActive}
      alignment={alignment}
      justifyContent={justifyContent}
      sx={sx}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === MuiTypography) {
          return React.cloneElement(child, {
            variant: child.props.variant || 'h5',
            ...child.props
          });
        }
        return child;
      })}
    </SubtitleContainer>
  );
}

const ContentInner = styled(Stack) <{ alignment: Alignment; justifyContent: string }>`
  flex: 1;
  width: 100%;
  min-height: 0;
  display: flex;
  text-align: ${({ alignment }) => alignment};
  justify-content: ${({ justifyContent }) => justifyContent};
`;
export const Content = ({
  children,
  alignment = "left",
  justifyContent = "flex-start",
  sx,
}: {
  children: React.ReactNode;
  alignment?: Alignment;
  justifyContent?: string;
  sx?: SxProps<Theme>
}) => {
  return (
    <ContentInner alignment={alignment} justifyContent={justifyContent} sx={sx}>
      {children}
    </ContentInner>
  )
}

const FooterContainerInner = styled(Stack)`
  height: 4rem;
  width: 100%;
  padding: 0.75rem 0;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;
export const FooterContainer = ({ children, sx }: { children: React.ReactNode, sx?: SxProps<Theme> }) => {
  return <FooterContainerInner sx={sx}>{children}</FooterContainerInner>;
}
export const FooterPaging = LayoutFooterPaging;
export const FooterSubmit = LayoutFooterSubmit;

const MainContainer = styled(Stack) <{ isMiniTablet: boolean; backgroundImage?: string; backgroundColor?: string }>`
  width: 100%;
  height: 100vh;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: ${({ isMiniTablet }) => (isMiniTablet ? "0 1.25rem" : "0 2.5rem")};
  box-sizing: border-box;
  position: relative;
  background-image: ${({ backgroundImage }) => (backgroundImage ? `url(${backgroundImage})` : "none")};
  background-size: cover;
  background-position: center;
  background-color: ${({ backgroundColor }) => backgroundColor ?? "#fff"};
  overflow-y: hidden;
`;

const SubtitleContainer = styled(Stack) <{ isSticky: boolean; alignment: Alignment; justifyContent: string }>`
  height: 5rem;
  width: 100%;
  padding: 1.25rem 2.5rem;
  display: flex;
  align-items: ${({ alignment }) => alignment};
  justify-content: ${({ justifyContent }) => justifyContent};
  background-color: ${({ isSticky }) => (isSticky ? "#fff" : "none")};
  position: ${({ isSticky }) => (isSticky ? "sticky" : "relative")};
  top: ${({ isSticky }) => (isSticky ? "0" : "auto")};
  left: 0;
  z-index: 10;
  transition: all 0.2s ease-in-out;
  box-shadow: ${({ isSticky }) => (isSticky ? "0 0.125rem 0.3125rem rgba(0, 0, 0, 0.1)" : "none")};
`;


const ContentContainerWrapper = styled(MuiStack)({
  position: 'relative',
  width: '100%',
  height: '100%',
});

const ScrollMoreButton = styled(Button)`
  position: absolute;
  bottom: 1.25rem;
  right: 2.875rem;
  border-radius: 50%;
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  z-index: 20;
`;

